import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px 80px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
    background-size: cover;
    background-position: center;
    opacity: 0.1;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
  text-align: center;
`;

const FeaturedSection = styled.section`
  padding: 100px 20px;
  background: white;
`;

const SectionLabel = styled.div`
  color: #ffd700;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 20px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  color: #333;
  margin-bottom: 60px;
  text-align: center;
`;

const FeaturedCard = styled.div`
  background: #f8f9fa;
  border-radius: 20px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto 40px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  }
`;

const ShowTitle = styled.h3`
  font-size: 28px;
  font-weight: 700;
  color: #232B3B;
  margin-bottom: 10px;
`;

const ShowDate = styled.div`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
`;

const ShowDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #333;
  margin-bottom: 30px;
`;

const ShowLinks = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const ShowLink = styled.a`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(35, 43, 59, 0.3);
  }
  
  &::after {
    content: '→';
    font-size: 18px;
  }
`;

const DocumentaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 900px;
  margin: 0 auto;
`;

const DocumentaryCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const DocumentaryTitle = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const DocumentaryYear = styled.div`
  font-size: 14px;
  color: #666;
`;

const PressInquiriesSection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
`;

const InquiriesBox = styled.div`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`;

const InquiriesTitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 800;
  margin-bottom: 30px;
`;

const InquiriesText = styled.p`
  font-size: 20px;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.9;
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContactItem = styled.div`
  font-size: 18px;
  
  strong {
    color: #ffd700;
    margin-right: 10px;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      color: #ffd700;
    }
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;
`;

const NewsCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const NewsDate = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`;

const NewsTitle = styled.h4`
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
`;

const NewsExcerpt = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

// Fill these in to show the buttons for the Tom Hegna Financial Freedom episode
const HEGNA_INTERVIEW_URL = '';
const HEGNA_TRANSCRIPT_URL = '';

export const MediaPress: React.FC = () => {
  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <PageTitle>Media & Press</PageTitle>
          <PageSubtitle>
            Sharing insights and expertise to help Americans make better retirement decisions
          </PageSubtitle>
        </Container>
      </HeroSection>

      <FeaturedSection>
        <Container>
          <SectionLabel>Featured Appearances</SectionLabel>
          <SectionTitle>Recent Media Highlights</SectionTitle>
          
          <FeaturedCard>
            <ShowTitle>Financial Freedom with Tom Hegna</ShowTitle>
            <ShowDate>August 7, 2025</ShowDate>
            <ShowDescription>
              Cap Averill II reveals how AI is exposing the truth about annuity returns and 
              why most advisors won't tell you about the 2.99% managed volatility trap.
            </ShowDescription>
            {/* Hidden until the real interview and transcript links are available */}
            {(HEGNA_INTERVIEW_URL || HEGNA_TRANSCRIPT_URL) && (
              <ShowLinks>
                {HEGNA_INTERVIEW_URL && (
                  <ShowLink href={HEGNA_INTERVIEW_URL} target="_blank" rel="noopener noreferrer">
                    Watch the Interview
                  </ShowLink>
                )}
                {HEGNA_TRANSCRIPT_URL && (
                  <ShowLink href={HEGNA_TRANSCRIPT_URL} target="_blank" rel="noopener noreferrer">
                    Read Transcript
                  </ShowLink>
                )}
              </ShowLinks>
            )}
          </FeaturedCard>
        </Container>
      </FeaturedSection>

      <PressInquiriesSection>
        <Container>
          <InquiriesBox>
            <InquiriesTitle>Press Inquiries</InquiriesTitle>
            <InquiriesText>
              For media requests, expert commentary, or speaking engagements:
            </InquiriesText>
            <ContactInfo>
              <ContactItem>
                <strong>Contact:</strong>
                <a href="mailto:media@annuityanswers.com">media@annuityanswers.com</a>
              </ContactItem>
              <ContactItem>
                <strong>Phone:</strong>
                <a href="tel:800-922-1894">800-922-1894</a>
              </ContactItem>
            </ContactInfo>
          </InquiriesBox>
        </Container>
      </PressInquiriesSection>
    </SharedLayout>
  );
};